import { createEvent, Effect, EventCallable, sample, type Store } from 'effector';
import { RequestStrategyEnum, RestApiError } from '@shared/api';

const API_DELAY = 300;

export type WrapperApiOnceOptions<Data = unknown> = {
  $data: Store<Data>;
  hasData?: (data: Data) => boolean;
};

const defaultHasData = (data: unknown): boolean => {
  if (Array.isArray(data)) return data.length > 0;
  if (data && typeof data === 'object') return Object.keys(data).length > 0;
  return Boolean(data);
};

//**In case of abort strategy usage also used abortApiFailCaseTrigger if fail case of effect to prevent error events or loader blinks*/
//**In case of abort strategy usage to prevent error messages from store also use errorStoreAbortRequestWatcher in $errorStore.watch */
//**Once strategy skips the request when store already has data or the effect is already pending*/

export class ApiUtils {
  static wrapperApi<Req, Res>(
    effect: Effect<Req, Res, Error>,
    strategy: typeof RequestStrategyEnum.Once,
    onceOptions: WrapperApiOnceOptions,
  ): { trigger: EventCallable<Req>; cancel: EventCallable<void> };
  static wrapperApi<Req, Res>(
    effect: Effect<Req, Res, Error>,
    strategy?: Exclude<RequestStrategyEnum, typeof RequestStrategyEnum.Once>,
  ): { trigger: EventCallable<Req>; cancel: EventCallable<void> };
  static wrapperApi<Req, Res>(
    effect: Effect<Req, Res, Error>,
    strategy: RequestStrategyEnum = RequestStrategyEnum.Default,
    onceOptions?: WrapperApiOnceOptions,
  ): { trigger: EventCallable<Req>; cancel: EventCallable<void> } {
    const triggerEvent = createEvent<Req>();
    const cancelEvent = createEvent<void>();

    if (strategy === RequestStrategyEnum.Once) {
      if (!onceOptions) throw new Error('Once strategy requires onceOptions with $data store');

      const hasData = onceOptions.hasData ?? defaultHasData;
      const { trigger: innerTrigger, cancel: innerCancel } = ApiUtils.wrapperApi(effect, RequestStrategyEnum.Debounce);

      sample({
        clock: triggerEvent,
        source: { data: onceOptions.$data, pending: effect.pending },
        filter: ({ data, pending }) => !hasData(data) && !pending,
        fn: (_, request) => request,
        target: innerTrigger,
      });

      cancelEvent.watch(() => innerCancel());

      return { trigger: triggerEvent, cancel: cancelEvent };
    }

    let timeoutId: NodeJS.Timeout | null = null;
    const uniqueRequests = new Set<string>();
    const uniqueRequestsList: Req[] = [];
    let abortController: AbortController | null = null;

    triggerEvent.watch(async (request) => {
      const requestKey = JSON.stringify(request);
      const requestObject = request;

      switch (strategy) {
        case RequestStrategyEnum.Default: {
          if (!uniqueRequests.has(requestKey)) {
            uniqueRequests.add(requestKey);
            const result = effect(requestObject);
            if (result && typeof result.finally === 'function') result.finally(() => uniqueRequests.delete(requestKey));
            else uniqueRequests.delete(requestKey);
          }
          break;
        }
        case RequestStrategyEnum.Debounce: {
          const requestKey = JSON.stringify(request);

          if (!uniqueRequests.has(requestKey)) {
            uniqueRequests.add(requestKey);
            uniqueRequestsList.push(request);

            if (timeoutId) clearTimeout(timeoutId);

            timeoutId = setTimeout(() => {
              uniqueRequestsList.forEach((req) => {
                const result = effect(req);
                if (result && typeof result.finally === 'function') result.finally(() => uniqueRequests.delete(requestKey));
                else uniqueRequests.delete(requestKey);
              });
              uniqueRequestsList.length = 0;
            }, API_DELAY);
          }
          break;
        }
        case RequestStrategyEnum.Abort: {
          if (abortController) abortController.abort();
          abortController = new AbortController();
          const result = effect({ ...request, signal: abortController.signal });
          if (result && typeof result.finally === 'function') result.finally(() => uniqueRequests.delete(requestKey));
          else uniqueRequests.delete(requestKey);
          break;
        }
      }
    });

    cancelEvent.watch(() => {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    });

    return { trigger: triggerEvent, cancel: cancelEvent };
  }

  static isAbortError = (error: Error) => error.name === 'AbortError';

  static abortApiLoadingFailCaseTrigger = (error: Error) => ApiUtils.isAbortError(error);

  static abortApiErrorFailCaseTrigger = (error: Error, message?: string) => {
    if (ApiUtils.isAbortError(error)) return '';
    return message ?? error.message;
  };

  static getApiErrorData = (error: Error, defaultMessage: string = 'Something went wrong. Please try again.') => {
    if (!RestApiError.isRestApiError(error)) return { message: defaultMessage };

    const { errorCode, innerErrors, message } = error.data;

    if (innerErrors?.length) return { errorCode, message: innerErrors.map((innerError) => innerError.message).join('\n') };
    if (errorCode) return { errorCode, message };
    if (message) return { message };
    return { message: defaultMessage };
  };
}
